import type { InventoryItem, InventoryItemUpdate } from "./types";
import { inventoryAgeDays } from "./inventoryFacts";

export const stockAdjustmentReasons = {
  count: "Correct a stock count",
  opening: "Confirm opening stock",
  restock: "Stock received",
  damage: "Lost or damaged stock"
} as const;

export interface StockAdjustment {
  operationID: string;
  expectedUpdatedAt: string;
  quantity: number;
  reason: keyof typeof stockAdjustmentReasons;
}

export interface StockChange extends StockAdjustment {
  previousQuantity: number;
  actorUserID: string;
  createdAt: string;
}

export class InventoryItemConflictError extends Error {}

export function stockReviewIsDue(item: InventoryItem, now = new Date()): boolean {
  return item.status !== "sold" && item.status !== "archived" && item.quantity > 0 &&
    (inventoryAgeDays(item, now) ?? -1) >= 60 &&
    (!item.stockReviewAfter || Date.parse(item.stockReviewAfter) <= now.getTime());
}

export function applyStockUpdate(item: InventoryItem, update: InventoryItemUpdate, actorUserID: string, now: Date): { item: InventoryItem; replayed?: boolean } {
  if (update.stockAdjustment !== undefined) {
    const adjustment = update.stockAdjustment;
    if (!adjustment || typeof adjustment !== "object" ||
      typeof adjustment.operationID !== "string" || !/^[a-zA-Z0-9_-]{16,100}$/.test(adjustment.operationID) ||
      !Number.isSafeInteger(adjustment.quantity) || adjustment.quantity < 0 || adjustment.quantity > 999999 ||
      !Object.prototype.hasOwnProperty.call(stockAdjustmentReasons, adjustment.reason)) {
      throw new Error("Choose a reason and a whole stock count between 0 and 999,999.");
    }
    if (Object.keys(update).some((key) => key !== "stockAdjustment")) {
      throw new Error("Save stock corrections separately from other item changes.");
    }
    const previous = item.stockHistory?.find((change) => change.operationID === adjustment.operationID);
    if (previous) {
      if (previous.quantity !== adjustment.quantity || previous.reason !== adjustment.reason || previous.expectedUpdatedAt !== adjustment.expectedUpdatedAt) {
        throw new InventoryItemConflictError("This correction was already used. Refresh the item and review the stock again.");
      }
      return { item, replayed: true };
    }
    if (item.status === "sold" || item.status === "archived") {
      throw new InventoryItemConflictError("This item is sold or archived. Refresh the item before continuing.");
    }
    if (adjustment.expectedUpdatedAt !== item.updatedAt) {
      throw new InventoryItemConflictError("This item changed while you were reviewing it. Refresh and check the latest stock count.");
    }
    return { item: { ...item, quantity: adjustment.quantity, openingStockStatus: "confirmed", stockHistory: [
      { operationID: adjustment.operationID, expectedUpdatedAt: adjustment.expectedUpdatedAt, quantity: adjustment.quantity, reason: adjustment.reason, previousQuantity: item.quantity, actorUserID, createdAt: now.toISOString() },
      ...(item.stockHistory ?? [])
    ].slice(0, 200) } };
  }
  if (Object.prototype.hasOwnProperty.call(update, "stockReviewAfter")) {
    if (update.expectedUpdatedAt !== item.updatedAt) throw new InventoryItemConflictError("This item changed. Refresh it before scheduling the next review.");
    if (item.status === "sold" || item.status === "archived") throw new Error("Only active inventory needs a stock review.");
    const date = update.stockReviewAfter === null ? null : Date.parse(update.stockReviewAfter ?? "");
    if (date !== null && (!Number.isFinite(date) || date <= now.getTime() || date > now.getTime() + 90 * 86400000)) {
      throw new Error("Choose a review date within the next 90 days.");
    }
    return { item: { ...item, stockReviewAfter: date === null ? undefined : new Date(date).toISOString() } };
  }
  return { item };
}
